import * as PaymentConnectionsApi from '@/api/payment-connections'
import * as mutationTypes from '@/store/mutation-types'
import { PAYMENT_CONNECTION_STATE, PAYMENT_PROVIDER_KEYS, getCurrenciesForProvider } from '@/shared/constants/payment'

const PROVIDERS = [PAYMENT_PROVIDER_KEYS.STRIPE, PAYMENT_PROVIDER_KEYS.PAYPAL]

const normaliseConnection = (provider, connection = {}) => {
  const providerAccountId = connection.provider_account_id || null
  const connected = Boolean(
    providerAccountId &&
    (connection.connected || connection.status === PAYMENT_CONNECTION_STATE.ACTIVE)
  )
  const hasAccount = Boolean(providerAccountId)
  const accountName = hasAccount
    ? connection.account_name || connection.provider_account_email || providerAccountId
    : ''
  const metadata = connection.metadata || {}
  const manuallyDisconnected = Boolean(metadata.manually_disconnected)

  return {
    provider,
    id: connection.id || connection.connection_id || null,
    connected,
    hasAccount,
    needsSetup: hasAccount && !connected && !manuallyDisconnected,
    status: connection.status || (connected ? PAYMENT_CONNECTION_STATE.ACTIVE : PAYMENT_CONNECTION_STATE.INACTIVE),
    account_name: accountName,
    provider_account_id: providerAccountId,
    metadata
  }
}

const normaliseConnections = (payload) => {
  const data = payload || {}
  return PROVIDERS.reduce((connections, provider) => {
    connections[provider] = normaliseConnection(provider, data[provider])
    return connections
  }, {})
}

const state = {
  connections: normaliseConnections(),
  currencies: {},
  loaded: false
}

const getters = {
  connections: (state) => state.connections,
  loaded: (state) => state.loaded,
  activeConnections: (state) => {
    return Object.values(state.connections).filter((connection) => connection.connected)
  },
  hasActiveConnection: (state, getters) => getters.activeConnections.length > 0,
  connectionByProvider: (state) => (provider) => {
    return state.connections[provider] || normaliseConnection(provider)
  },
  currenciesForProvider: (state) => (provider) => getCurrenciesForProvider(provider, state.currencies)
}

const actions = {
  fetchPaymentConnections({ commit, state }, options = {}) {
    if (state.loaded && !options.force) {
      return Promise.resolve(state.connections)
    }

    return PaymentConnectionsApi.getPaymentConnections()
      .then((response) => {
        const payload = response?.data?.data || response?.data || {}
        commit(mutationTypes.PAYMENT_CONNECTIONS_SET_CONNECTIONS, payload)
        commit(mutationTypes.PAYMENT_CONNECTIONS_SET_CURRENCIES, payload.currencies || {})
        return response
      })
  },
  connectStripe(context, data) {
    return PaymentConnectionsApi.connectStripe(data)
  },
  connectPayPal(context, data) {
    return PaymentConnectionsApi.connectPayPal(data)
  },
  disconnectPaymentConnection({ dispatch }, data) {
    return PaymentConnectionsApi.disconnectPaymentConnection(data.id)
      .then((response) => {
        return dispatch('fetchPaymentConnections', { force: true }).then(() => response)
      })
  }
}

const mutations = {
  [mutationTypes.PAYMENT_CONNECTIONS_SET_CONNECTIONS](state, connections) {
    state.connections = normaliseConnections(connections)
    state.loaded = true
  },
  [mutationTypes.PAYMENT_CONNECTIONS_SET_CURRENCIES](state, currencies) {
    state.currencies = currencies || {}
  },
  [mutationTypes.PAYMENT_CONNECTIONS_SET_CONNECTION](state, connection) {
    const provider = connection.provider
    if (!provider) {
      return
    }

    state.connections = {
      ...state.connections,
      [provider]: normaliseConnection(provider, connection)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
